"use client";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./Popup.css";
import { Constant, fontSizes } from "@/constants/constants";
import { Form, Input, Checkbox, notification } from "antd";
import { submitLeadForm } from "@/lib/submit-form";

// Shared styling so every field looks identical.
const inputClass =
  "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:!text-[#6B7280] placeholder:!opacity-100 hover:border-[#1EA7DB] focus:border-[#1EA7DB] focus:bg-white focus:ring-2 focus:ring-[#1EA7DB]/20 transition-colors";

export default function ButtonCta({
  classStyle,
  buttonText = "GET A QUOTE",
  icon,
  auto = false
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true); // for portal
    function handleScroll() {
      const scrollTop = window.pageYOffset;
      setIsScrolled(scrollTop > 500);
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [popupOpen, setPopupOpen] = useState(false);

  const closePopup = () => setPopupOpen(false);
  const openPopup = () => setPopupOpen(true);

  useEffect(() => {
    if (auto) {
      const timer = setTimeout(() => {
        openPopup();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [auto]);

  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    // Only send the user to the Thank You page when the lead actually went
    // through. On failure, keep the popup open and surface the error so the
    // user can retry instead of seeing a false "thank you".
    const res = await submitLeadForm(values, "Popup CTA — 50% Discount");
    if (res.ok) {
      window.location.assign("/thankyou");
      return;
    }
    setSubmitting(false);
    notification.error({
      message: "Could not send your message",
      description: res.message,
    });
  };

  return (
    <>
      {/* Popup rendered in portal */}
      {mounted && popupOpen &&
        createPortal(
          <div
            onClick={closePopup}
            className="popupOverlay fixed inset-0 z-[9999] flex justify-center items-center bg-black/50 backdrop-blur-sm p-4"
          >
            <div
              onClick={(e) => e.stopPropagation()} // 👈 prevent closing when clicking inside
              className="popupCard relative bg-white flex flex-col overflow-hidden rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
            >
              {/* Header banner */}
              <div className="relative bg-gradient-to-br from-[#1EA7DB] to-[#1888B4] px-6 py-8 text-center text-white">
                <span className="inline-block mb-3 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold tracking-wide uppercase">
                  Limited Time Offer
                </span>
                <h2 className={`${fontSizes.text_4_xl} font-extrabold leading-none`}>
                  50% Discount
                </h2>
                <h3 className={`mt-2 ${fontSizes.text_xl} font-semibold leading-snug`}>
                  Your Bestselling book is just a click away
                </h3>
                <p className={`mt-2 text-white/90 ${fontSizes.base}`}>
                  Let’s schedule a call and attain free consultancy.
                </p>
              </div>

              {/* Form */}
              <div className="w-full p-6 md:p-8">
                <Form
                  name="normal_login"
                  initialValues={{ remember: true }}
                  onFinish={onFinish}
                  requiredMark={false}
                >
                  <Form.Item
                    name="name"
                    className="!mb-4"
                    rules={[{ required: true, message: "Please input your Name!" }]}
                  >
                    <Input
                      type="text"
                      placeholder="Enter Your Full Name*"
                      className={inputClass}
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    className="!mb-4"
                    rules={[
                      { type: "email", message: "The input is not valid E-mail!" },
                      { required: true, message: "Please input your E-mail!" },
                    ]}
                  >
                    <Input
                      type="email"
                      placeholder="Enter Your Email Address*"
                      className={inputClass}
                    />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    className="!mb-2"
                    rules={[
                      {
                        pattern: /^\+?[\d\s\-\(\)]+$/,
                        message: "Please enter a valid phone number!",
                      },
                    ]}
                  >
                    <Input
                      type="tel"
                      placeholder="Enter Your Phone No"
                      className={inputClass}
                    />
                  </Form.Item>
                  <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-snug text-gray-500">
                    By submitting your phone number, you agree to receive SMS messages from {Constant.CompanyName} about your inquiry and services. Message frequency varies; message &amp; data rates may apply. Reply HELP for help or STOP to cancel. Consent isn&apos;t required to make a purchase.
                  </p>

                  <Form.Item
                    name="message"
                    className="!mb-5"
                    rules={[{ required: true, message: "Please input your message" }]}
                  >
                    <Input.TextArea
                      placeholder="To help us understand better, enter a brief description about your book."
                      className={inputClass}
                      rows={3}
                    />
                  </Form.Item>

                  <Form.Item name="smsConsent" valuePropName="checked" className="!mb-5">
                    <Checkbox className="items-start text-xs leading-relaxed text-gray-500 [&_.ant-checkbox]:mt-0.5">
                      I agree to receive SMS messages from {Constant.CompanyName} as described above. See our{" "}
                      <a href="/privacy-policy" target="_blank" className="text-[#1EA7DB] underline">Privacy Policy</a> and{" "}
                      <a href="/terms-and-conditions#sms-terms" target="_blank" className="text-[#1EA7DB] underline">Terms & Conditions</a>.
                    </Checkbox>
                  </Form.Item>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-[#1EA7DB] to-[#1888B4] p-3 rounded-full hover:shadow-lg hover:shadow-[#1EA7DB]/30 hover:-translate-y-0.5 transition-all duration-200 text-white font-semibold disabled:opacity-60 disabled:translate-y-0 disabled:shadow-none"
                  >
                    {submitting ? "Sending..." : "Submit"}
                  </button>
                </Form>
              </div>

              {/* Close Button */}
              <button
                onClick={closePopup}
                aria-label="Close"
                className="absolute top-3 right-3 flex items-center justify-center w-9 h-9 text-2xl leading-none border border-white/50 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
              >
                &times;
              </button>
            </div>
          </div>,
          document.body
        )
      }

      {/* Trigger button */}
      {!auto && (
        <button
          data-aos="fade-right"
          onClick={openPopup}
          className={`inline-flex items-center justify-center gap-2 px-8 py-3 shadow-lg font-bold rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${fontSizes.base} ${classStyle}`}
        >
          {buttonText} {icon}
        </button>
      )}
    </>
  );
}
